import React, { useEffect, useMemo, useState } from "react";
import { Box, MenuItem, Select } from "@mui/material";
import {
  tokens,
  fmtMoney,
  fmtNumber,
  SignalBar,
  StatusPill,
  ClinicalDataGrid,
  ClinicalPageShell,
} from "../../shared/clinical-design";
import SurveillanceDetailPanel from "../components/SurveillanceDetailPanel";
import { getSurveillancePanel } from "../services/surveillanceService";
import {
  LOCATIONS,
  DEFAULT_LOCATION,
  HEALTH_STATUS_META,
  riskBandFor,
} from "../common/constants";

const COLUMNS = [
  {
    key: "riskScore",
    header: "Score",
    align: "right",
    mono: true,
    width: 70,
    render: (r) => {
      const band = riskBandFor(r.riskScore);
      return (
        <Box component="span" sx={{ color: tokens[band.tone] || tokens.ink, fontWeight: 600 }}>
          {r.riskScore.toFixed(2)}
        </Box>
      );
    },
  },
  { key: "name", header: "Patient" },
  {
    key: "healthStatus",
    header: "Status",
    render: (r) => (
      <StatusPill
        tone={HEALTH_STATUS_META[r.healthStatus]?.tone || "muted"}
        label={r.healthStatus}
        dense
      />
    ),
  },
  { key: "openCareGaps", header: "Gaps", align: "right", mono: true, width: 60 },
  {
    key: "totalCost",
    header: "Cost 12m",
    align: "right",
    mono: true,
    render: (r) => fmtMoney(r.totalCost),
  },
  { key: "triggers", header: "Trig", align: "right", mono: true, width: 55 },
];

/**
 * Module B — Surveillance Panel (High Risk Patient Surveillance).
 * Cola de pacientes de alto riesgo sin care management, ordenada por score
 * compuesto, con detalle de drivers y acción recomendada.
 */
export default function HighRiskSurveillancePage() {
  const [locationKey, setLocationKey] = useState(DEFAULT_LOCATION);
  const [data, setData] = useState({ summary: {}, patients: [] });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getSurveillancePanel(locationKey)
      .then((d) => {
        if (!active) return;
        setData(d);
        setSelectedId(d.patients[0]?.cipId ?? null);
      })
      .catch(() => {
        if (!active) return;
        setData({ summary: {}, patients: [] });
        setSelectedId(null);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [locationKey]);

  const selectedRow = useMemo(
    () => data.patients.find((p) => p.cipId === selectedId) || null,
    [data, selectedId]
  );

  const signals = useMemo(() => {
    const rows = data.patients;
    const total = rows.length;
    const critical = rows.filter((r) => r.riskScore >= 0.75).length;
    const overdue = rows.filter((r) => r.touchpointOverdue).length;
    const costAtRisk = rows.reduce((sum, r) => sum + (r.totalCost ?? 0), 0);
    const onTrackPct = total ? Math.round(((total - overdue) / total) * 100) : 0;
    return [
      { label: "Panel size", value: fmtNumber(total), tone: "info" },
      {
        label: "Critical (≥0.75)",
        value: critical,
        tone: critical > 0 ? "crit" : "ok",
        hint: "Patients above the critical composite-risk threshold",
      },
      {
        label: "EOL / Complex no CM",
        value: (data.summary.eolNoCM ?? 0) + (data.summary.complexNoCM ?? 0),
        tone: "crit",
      },
      {
        label: "Touchpoints overdue",
        value: overdue,
        tone: overdue > 0 ? "warn" : "ok",
        hint: "High-risk patients without a surveillance contact in the last 30 days",
      },
      { label: "Cost at risk 12m", value: fmtMoney(costAtRisk), tone: "warn" },
      {
        label: "On cadence",
        value: `${onTrackPct}%`,
        tone: onTrackPct >= 70 ? "ok" : "warn",
      },
    ];
  }, [data]);

  const locationLabel =
    LOCATIONS.find((l) => l.key === locationKey)?.label || locationKey;

  return (
    <ClinicalPageShell
      title="Surveillance Panel"
      subtitle="High-risk patients without active care management, prioritized by composite risk score (acuity, gaps, cost, utilization, triggers)."
      moduleTag="Module B"
      devPreview
      actions={
        <Select
          size="small"
          value={locationKey}
          onChange={(e) => setLocationKey(e.target.value)}
          sx={{ fontSize: 13, minWidth: 170, bgcolor: tokens.surface }}
        >
          {LOCATIONS.map((l) => (
            <MenuItem key={l.key} value={l.key} sx={{ fontSize: 13 }}>
              {l.label}
            </MenuItem>
          ))}
        </Select>
      }
    >
      <SignalBar signals={signals} sx={{ mb: 1.5 }} />
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "flex-start",
          flexDirection: { xs: "column", lg: "row" },
        }}
      >
        <Box sx={{ width: { xs: "100%", lg: "45%" }, flexShrink: 0 }}>
          <ClinicalDataGrid
            columns={COLUMNS}
            rows={data.patients}
            rowKey={(r) => r.cipId}
            loading={loading}
            onRowClick={(r) => setSelectedId(r.cipId)}
            isRowSelected={(r) => r.cipId === selectedId}
            emptyMessage="No unmanaged high-risk patients for this location."
            maxHeight={620}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <SurveillanceDetailPanel
            key={selectedRow?.cipId ?? "none"}
            row={selectedRow}
            locationLabel={locationLabel}
          />
        </Box>
      </Box>
    </ClinicalPageShell>
  );
}
